/**
 * File: useTimerProviderController.ts
 * Path: apps/admin/src/shared/hooks/useTimerProviderController.ts
 * Description: The state behind the TimerProvider: the running timer (kept on
 * this device), starting, stopping, discarding.
 */

import { useCallback, useMemo, useState } from "react";
import { toast } from "@idk/ui";
import type { EffortRecord } from "../services/effortService";
import { readStored, writeStored, type RunningTimer } from "../utils/timerUtils";
import type { TimerApi } from "./useTimerState";

export const useTimerProviderController = () => {
  const [running, setRunning] = useState<RunningTimer | null>(readStored);
  const [stopping, setStopping] = useState(false);

  const start = useCallback(
    (record: EffortRecord) => {
      if (running) {
        toast.error(`A timer is already running for ${running.record.name}. Stop it first.`);
        return;
      }
      const next = { record, startedAt: Date.now() };
      writeStored(next);
      setRunning(next);
      toast.success(`Timer started for ${record.name}.`);
    },
    [running],
  );

  const clear = useCallback(() => {
    writeStored(null);
    setRunning(null);
    setStopping(false);
  }, []);

  const discard = () => {
    if (window.confirm("Discard this timer without saving the time?")) clear();
  };

  const api = useMemo<TimerApi>(() => ({ running, start }), [running, start]);

  return {
    api,
    running,
    stopping,
    beginStop: () => setStopping(true),
    cancelStop: () => setStopping(false),
    clear,
    discard,
  };
};
